import { useRouter } from "next/navigation";
import { getUserData, updateUserData } from "../_actions/user.actions";
import { clientLogger } from "@/lib/logger";
import { Dispatch, SetStateAction } from "react";

export const updateUser = async ({
	firstName,
	lastName,
	ctc,
	setData,
	companyRoleId,
	referralCode,
	router,
}: {
	firstName?: string;
	lastName?: string;
	ctc?: number;
	companyRoleId?: string;
	referralCode?: string;
	setData?: (values: Partial<User>) => void;
	router?: ReturnType<typeof useRouter>;
}): Promise<{ success: boolean; data?: User; error?: string }> => {
	try {
		// Check if at least one updatable field is provided
		if (
			firstName === undefined &&
			lastName === undefined &&
			ctc === undefined
		) {
			return {
				success: false,
				error:
					"At least one field (firstName, lastName, or ctc) must be provided",
			};
		}

		// Create update payload with only defined values
		const updatePayload: {
			firstName?: string;
			lastName?: string;
			ctc?: number;
			companyRoleId?: string;
			referralCode?: string;
		} = {};

		if (firstName !== undefined) updatePayload.firstName = firstName;
		if (lastName !== undefined) updatePayload.lastName = lastName;
		if (ctc !== undefined) updatePayload.ctc = ctc;
		if (companyRoleId !== undefined)
			updatePayload.companyRoleId = companyRoleId;
		if (referralCode !== undefined) updatePayload.referralCode = referralCode;

		const data = await updateUserData(updatePayload);

		if (data) {
			// Only refresh and update state if successful
			if (router) {
				router.refresh();
			}
			if (setData) {
				// Only update state with the fields that were actually updated
				const stateUpdate: Partial<{
					firstName: string;
					lastName: string;
					ctc: number;
				}> = {};
				if (firstName !== undefined) stateUpdate.firstName = firstName;
				if (lastName !== undefined) stateUpdate.lastName = lastName;
				if (ctc !== undefined) stateUpdate.ctc = ctc;
				setData(stateUpdate);
			}
			return { success: true, data };
		} else {
			return { success: false, error: "No data returned from server" };
		}
	} catch (error) {
		clientLogger.error("Failed to update user:", error as Error);

		// Fixed error handling - removed the problematic type assertion
		let errorMessage = "Failed to update user details";

		if (error instanceof Error) {
			// If your error object has a nested data property, handle it properly
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const errorObj = error as any;
			errorMessage =
				errorObj?.response?.data?.message ||
				errorObj?.data?.message ||
				error.message ||
				errorMessage;
		}

		return {
			success: false,
			error: errorMessage,
		};
	}
};

export const fetchUserData = async ({
	setUser,
}: {
	setUser: Dispatch<SetStateAction<User | undefined>>;
}): Promise<User | undefined> => {
	const user = await getUserData();
	clientLogger.info("Fetched user data", user);
	setUser(user);
	return user;
};
